import {
  Button,
  DatePicker,
  Input,
  InputStepper,
  Select,
  Text,
  Stack,
  VStack,
} from '@genial/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  SignedInNavigationScreenNavigationProp,
  SignedInRootStackParamList,
} from '../../../../../../../../../../routes/types';
import { usePrivatePensionAssetController } from '../../../../../../../../../BatchTicket/components/DynamicAsset/hooks/products/PrivatePension';
import { PrivatePensionBeneficiary } from '../../../../../../../../../BatchTicket/components/DynamicAsset/hooks/products/PrivatePension/types';
import { PrivatePensionModalContainer } from '../PrivatePensionModalContainer';
import { PrivatePensionModalHeader } from '../PrivatePensionModalHeader';

export namespace PrivatePensionBeneficiaryModalTypes {
  export interface Props {}
}

export const PrivatePensionBeneficiaryModal: React.FC<
  PrivatePensionBeneficiaryModalTypes.Props
> = () => {
  const navigation = useNavigation<SignedInNavigationScreenNavigationProp>();
  const route =
    useRoute<RouteProp<SignedInRootStackParamList, 'CustomerDetailPrivatePensionBeneficiary'>>();
  const { index, beneficiary, offer, accountId, accountNumber } = route.params;
  const { relatedPartyOptions, addBeneficiary, updateBeneficiary, maxParticipationPercentage } =
    usePrivatePensionAssetController(offer, accountId, accountNumber);

  const maxPercentage = useMemo(() => {
    return index === undefined
      ? maxParticipationPercentage
      : beneficiary?.participationPercentage || maxParticipationPercentage;
  }, [maxParticipationPercentage, index, beneficiary]);

  const beneficiarySchema = useMemo(() => {
    return z.object({
      name: z
        .string({
          required_error: 'Nome é obrigatório',
        })
        .refine(
          (value) => {
            const regex = /^[\p{L}0-9']+(?: [\p{L}0-9']+)+$/u;
            return regex.test(value);
          },
          {
            message:
              'Nome e sobrenome são obrigatórios, não pode conter espaços consecutivos e não pode conter caracteres especiais',
          }
        ),
      relatedPartyId: z.number({
        required_error: 'Parentesco é obrigatório',
      }),
      birthDate: z
        .date({
          required_error: 'Data de nascimento é obrigatória',
        })
        .max(new Date(), {
          message: 'Data de nascimento não pode ser futura',
        }),
      participationPercentage: z
        .number({
          required_error: 'Participação é obrigatória',
        })
        .min(1, {
          message: 'Participação deve ser maior ou igual a 1%',
        })
        .max(maxPercentage, {
          message: `Participação deve ser menor ou igual a ${maxPercentage}%`,
        }),
    });
  }, [maxPercentage]);

  const formEdit = useForm({
    resolver: zodResolver(beneficiarySchema),
    mode: 'all',
    defaultValues: {
      name: beneficiary?.name || '',
      relatedPartyId: beneficiary?.relatedPartyId || undefined,
      birthDate: beneficiary?.birthDate || undefined,
      participationPercentage: beneficiary?.participationPercentage || 0,
    },
  });

  const {
    setValue,
    formState: { isValid, errors },
    control,
    getValues,
    handleSubmit,
  } = formEdit;

  const title = index === undefined ? 'Incluir Beneficiário' : 'Editar Beneficiário';
  const { name, relatedPartyId, birthDate, participationPercentage } = getValues();

  // used only to trigger validation
  const onSubmit = handleSubmit(() => {});

  const handleSelectBirthDate = useCallback(
    (date: Date) => {
      setValue('birthDate', date, {
        shouldValidate: true,
      });
    },
    [setValue]
  );

  const handleClearBirthDate = useCallback(() => {
    setValue('birthDate', undefined, {
      shouldValidate: true,
    });
  }, [setValue]);

  const handleSelectRelatedParty = useCallback(
    (value: string) => {
      setValue('relatedPartyId', Number(value), {
        shouldValidate: true,
      });
    },
    [setValue]
  );

  const handleCancel = () => {
    navigation.goBack();
  };

  const handleSaveBeneficiary = () => {
    if (index === undefined) {
      addBeneficiary(formEdit.getValues() as unknown as PrivatePensionBeneficiary);
    } else {
      updateBeneficiary(index, formEdit.getValues() as unknown as PrivatePensionBeneficiary);
    }

    navigation.goBack();
  };

  const handleParticipationPercentageChange = useCallback(
    (value: number) => {
      setValue('participationPercentage', value, {
        shouldValidate: true,
      });
    },
    [setValue]
  );

  useEffect(() => {
    if (index !== undefined) {
      onSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PrivatePensionModalContainer>
      <VStack space="stack.nano" width="100%">
        <PrivatePensionModalHeader title={title} onClose={handleCancel} />
        <Stack direction={{ base: 'column', md: 'row' }} space="stack.nano" width="100%" zIndex={1}>
          <VStack space="stack.quarck" flex={1}>
            <Text variant="semibold" fontSize="md">
              Nome completo
            </Text>
            <Input
              name="name"
              placeholder="Digite o nome do beneficiário"
              variant="filled"
              autoComplete="off"
              value={name}
              control={control}
              hasShadow={false}
              error={errors.name}
            />
          </VStack>
          <VStack space="stack.quarck" flex={1} zIndex={3}>
            <Text variant="semibold" fontSize="md">
              Data de nascimento
            </Text>
            <DatePicker
              placeholder="Selecione a data de nascimento"
              date={birthDate || undefined}
              onSelectDate={handleSelectBirthDate}
              onClear={handleClearBirthDate}
              availableRange={{
                future: new Date(),
              }}
              error={errors.birthDate}
            />
          </VStack>
        </Stack>
        <Stack direction={{ base: 'column', md: 'row' }} space="stack.nano" width="100%">
          <VStack space="stack.quarck" flex={1}>
            <Text variant="semibold" fontSize="md">
              Parentesco
            </Text>
            <Select
              placeholder="Selecione o parentesco"
              error={errors.relatedPartyId}
              options={relatedPartyOptions}
              selectedValue={relatedPartyId?.toString() || ''}
              onValueChange={handleSelectRelatedParty}
            />
          </VStack>
          <VStack space="stack.quarck" flex={1}>
            <Text variant="semibold" fontSize="md">
              Participação
            </Text>
            <InputStepper
              stepperValue={1}
              min={1}
              max={100}
              name="participationPercentage"
              onChange={handleParticipationPercentageChange}
              defaultValue={participationPercentage}
              customError={errors.participationPercentage?.message}
            />
          </VStack>
        </Stack>
        <Stack
          direction={{ base: 'column', md: 'row' }}
          width="100%"
          justifyContent={{ base: 'center', md: 'flex-end' }}
          flexDirection={{ base: 'column-reverse', md: 'row' }}
          space="stack.nano">
          <Button
            variant="secondary"
            onPress={handleCancel}
            testID="private-pension-portability-modal-cancel-button">
            Cancelar
          </Button>
          <Button
            isDisabled={!isValid}
            testID="private-pension-portability-modal-confirm-button"
            onPress={handleSaveBeneficiary}>
            Confirmar
          </Button>
        </Stack>
      </VStack>
    </PrivatePensionModalContainer>
  );
};
